import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import Dropzone from "dropzone"

// Disable Dropzone auto-discover to prevent conflicts
Dropzone.autoDiscover = false

export default class extends Controller {
  static targets = ["dropArea", "preview", "submit", "progress", "progressBar", "error"]
  static values = {
    url: String,
    maxFileSize: { type: Number, default: 10 },
    acceptedFiles: { type: String, default: "image/*,.pdf" },
    paramName: { type: String, default: "voucher" },
  }

  connect() {
    this.file = null
    this.initDropzone()
  }

  disconnect() {
    if (this.dropzone) {
      this.dropzone.destroy()
    }
  }

  initDropzone() {
    this.dropzone = new Dropzone(this.element, {
      url: this.urlValue,
      autoProcessQueue: false,
      uploadMultiple: false,
      maxFiles: 1,
      maxFilesize: this.maxFileSizeValue,
      acceptedFiles: this.acceptedFilesValue,
      clickable: this.hasDropAreaTarget ? this.dropAreaTarget : this.element,
      previewsContainer: false,
      createImageThumbnails: false,
      dictDefaultMessage: "",
      dictFallbackMessage: "",
      dictInvalidFileType: "This file type is not allowed. Please upload an image or PDF.",
      dictFileTooBig: `File is too large. Maximum size is ${this.maxFileSizeValue}MB.`,
      dictMaxFilesExceeded: "Only one file can be uploaded.",
    })

    this.setupEventListeners()
  }

  setupEventListeners() {
    this.dropzone.on("addedfile", (file) => {
      // Remove previous file if exists
      if (this.file) {
        this.dropzone.removeFile(this.file)
      }

      this.file = file
      this.hideError()
      this.showPreview(file)
      this.showSubmitButton()
    })

    this.dropzone.on("removedfile", () => {
      this.file = null
      this.hidePreview()
      this.hideSubmitButton()
    })

    this.dropzone.on("error", (file, message) => {
      this.dropzone.removeFile(file)
      this.showError(typeof message === "string" ? message : message.error || "Upload failed")
    })

    // Handle drag states for visual feedback
    this.dropzone.on("dragenter", () => {
      this.element.classList.add("border-blue-400", "bg-blue-50")
    })

    this.dropzone.on("dragleave", () => {
      this.element.classList.remove("border-blue-400", "bg-blue-50")
    })

    this.dropzone.on("drop", () => {
      this.element.classList.remove("border-blue-400", "bg-blue-50")
    })
  }

  showPreview(file) {
    if (!this.hasPreviewTarget) return

    this.previewTarget.innerHTML = ""
    this.previewTarget.classList.remove("hidden")

    if (this.hasDropAreaTarget) {
      this.dropAreaTarget.classList.add("hidden")
    }

    const previewContainer = document.createElement("div")
    previewContainer.className = "flex flex-col items-center gap-4"

    if (file.type.startsWith("image/")) {
      const img = document.createElement("img")
      img.className = "max-h-48 max-w-full rounded-lg border border-gray-200"
      const reader = new FileReader()
      reader.onload = (e) => {
        img.src = e.target.result
      }
      reader.readAsDataURL(file)
      previewContainer.appendChild(img)
    } else {
      // PDF or other file type icon
      const iconContainer = document.createElement("div")
      iconContainer.className = "flex items-center gap-3 p-4 bg-gray-100 rounded-lg"
      iconContainer.innerHTML = `
        <svg class="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
        </svg>
        <span class="text-sm font-medium text-gray-700">${this.escapeHtml(file.name)}</span>
      `
      previewContainer.appendChild(iconContainer)
    }

    // File info and remove button
    const fileInfo = document.createElement("div")
    fileInfo.className = "flex items-center gap-4 text-sm text-gray-500"
    fileInfo.innerHTML = `
      <span>${this.escapeHtml(file.name)} (${this.formatFileSize(file.size)})</span>
      <button type="button" class="text-red-500 hover:text-red-700 underline" data-action="click->dropzone#removeFile">
        Remove
      </button>
    `
    previewContainer.appendChild(fileInfo)

    this.previewTarget.appendChild(previewContainer)
  }

  hidePreview() {
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden")
      this.previewTarget.innerHTML = ""
    }

    if (this.hasDropAreaTarget) {
      this.dropAreaTarget.classList.remove("hidden")
    }
  }

  showSubmitButton() {
    if (this.hasSubmitTarget) {
      this.submitTarget.classList.remove("hidden")
    }
  }

  hideSubmitButton() {
    if (this.hasSubmitTarget) {
      this.submitTarget.classList.add("hidden")
    }
  }

  showProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove("hidden")
    }
  }

  hideProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.add("hidden")
    }
  }

  updateProgress(percent) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden")
      this.errorTarget.textContent = ""
    }
  }

  removeFile(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.file) {
      this.dropzone.removeFile(this.file)
    }
  }

  upload(event) {
    event.preventDefault()

    if (!this.file) {
      this.showError("Please select a file first")
      return
    }

    this.disableSubmit()
    this.showProgress()
    this.updateProgress(0)

    // Use ActiveStorage Direct Upload
    const upload = new DirectUpload(
      this.file,
      "/rails/active_storage/direct_uploads",
      this
    )

    upload.create((error, blob) => {
      if (error) {
        this.handleUploadError(error)
      } else {
        this.submitForm(blob.signed_id)
      }
    })
  }

  // DirectUpload delegate method for progress tracking
  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", (event) => {
      const progress = (event.loaded / event.total) * 100
      this.updateProgress(progress)
    })
  }

  submitForm(signedId) {
    const form = document.createElement("form")
    form.method = "POST"
    form.action = this.urlValue
    form.style.display = "none"

    // CSRF token
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    if (csrfToken) {
      const csrfInput = document.createElement("input")
      csrfInput.type = "hidden"
      csrfInput.name = "authenticity_token"
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    // Method override for PATCH
    const methodInput = document.createElement("input")
    methodInput.type = "hidden"
    methodInput.name = "_method"
    methodInput.value = "PATCH"
    form.appendChild(methodInput)

    // Signed blob ID
    const blobInput = document.createElement("input")
    blobInput.type = "hidden"
    blobInput.name = this.paramNameValue
    blobInput.value = signedId
    form.appendChild(blobInput)

    document.body.appendChild(form)
    form.submit()
  }

  handleUploadError(error) {
    this.hideProgress()
    this.enableSubmit()
    this.showError(error.message || "Upload failed. Please try again.")
    console.error("Direct upload error:", error)
  }

  disableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
  }

  enableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
      this.submitTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
